import { useQuery } from "@tanstack/react-query";
import { api, type Api } from "@api/bot-base-api-request";
import { PositionsBotApiResponse, WalletPositionsBot } from "./types";
import {
  BotWalletSupportedChain,
  Wallet,
  WalletType,
} from "@/providers/wallet";
import { LOGO_URL } from "@/constants";

export const POSITIONS_QUERY_KEY = "positions-bot";

const fetchPositions = async (
  api: Api,
  userId: string,
  wallet: Wallet,
): Promise<PositionsBotApiResponse> => {
  return api
    .get("pnl", {
      searchParams: {
        userId,
        walletAddress: wallet.address,
        chainId:
          wallet.walletType === WalletType.ETH
            ? BotWalletSupportedChain.ETH
            : BotWalletSupportedChain.SOL,
      },
    })
    .json();
};

const calculateRealizedPnl = (item: WalletPositionsBot): number => {
  if (item.totalSold === 0) return 0;
  const currentValue = item.balance * parseFloat(item.priceUsd);
  const costBasisRemaining = item.balance * item.boughtPriceAverage;
  const unrealizedPnl = currentValue - costBasisRemaining;

  return item.pnlValue - unrealizedPnl;
};

const calculateSoldValueUsd = (item: WalletPositionsBot): number => {
  if (item.totalSold === 0) return 0;
  const costBasisSold = item.totalSold * item.boughtPriceAverage;
  const realizedPnl = calculateRealizedPnl(item);

  return costBasisSold + realizedPnl;
};

const useGetBotPositions = (
  userId: string,
  wallet: Wallet,
  refetchInterval: number | undefined = undefined,
) => {
  return useQuery({
    queryKey: [POSITIONS_QUERY_KEY, userId, wallet.address, wallet.chain],
    queryFn: async () => fetchPositions(api, userId, wallet),
    refetchInterval,
    select: (data) =>
      data?.positions.map((item) => {
        const realizedPnl = calculateRealizedPnl(item);
        const soldUsd = calculateSoldValueUsd(item);

        return {
          balance: item.balance,
          max_balance: item.totalBought,
          token_address: item.address,
          logo: `${LOGO_URL}/${wallet.walletType}/${item.address}.jpg`,
          token_symbol: item.tokenInfo.symbol,
          token_name: item.tokenInfo.name,
          decimals: item.tokenInfo.decimals,
          invested: item.invested,
          sold: soldUsd,
          realized_pnl: realizedPnl,
          net_pnl: item.pnlUsd,
          net_pnl_percentage: item.pnlPercent,
          duration: item.sinceTimestamp,
          priceUsd: parseFloat(item.priceUsd),
        };
      }),
  });
};

export { useGetBotPositions };
